import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { IconInfoCircle, IconArrowRight } from '@tabler/icons-react';
import TopBar from '../../components/layout/TopBar';
import StickyFooter from '../../components/layout/StickyFooter';
import { Button, ErrorState, Skeleton } from '../../components/common';
import DoctorSummaryCard from '../../components/booking/DoctorSummaryCard';
import BookingScheduler from '../../components/booking/BookingScheduler';
import appointmentApi from '../../api/appointment/appointmentApi';
import doctorApi from '../../api/doctor/doctorApi';
import useFetch from '../../hooks/useFetch';
import useSlotPicker from '../../hooks/useSlotPicker';
import { useClinic } from '../../context/ClinicContext';
import { useToast } from '../../context/ToastContext';
import { PATHS, toPath } from '../../routes/paths';
import { formatDayDate, formatTime } from '../../utils/date';

export default function ReschedulePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { clinic } = useClinic();
  const appt = useFetch(() => appointmentApi.get(id), [id]);
  const doctorId = appt.data?.doctor?.id;
  const doctor = useFetch(() => doctorApi.get(doctorId), [doctorId], { skip: !doctorId });
  const picker = useSlotPicker(doctorId, { excludeAppointmentId: id, bookingWindowDays: clinic?.bookingWindowDays });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const a = appt.data;
  const { selectedDate, selectedTime } = picker;
  const unchanged = a && selectedDate === a.date && selectedTime === a.time;

  const save = async () => {
    setSubmitting(true);
    setSubmitError('');
    try {
      await appointmentApi.reschedule(id, { date: selectedDate, time: selectedTime });
      toast.show('Reschedule request sent');
      navigate(toPath(PATHS.appointmentDetail, { id }), { replace: true });
    } catch (e) {
      setSubmitError(e.message);
      setSubmitting(false);
    }
  };

  if (appt.error) {
    return (
      <div className="page">
        <TopBar title="Reschedule" bordered />
        <ErrorState error={appt.error} onRetry={appt.reload} />
      </div>
    );
  }

  return (
    <div className="page page--with-footer-lg booking">
      <TopBar title="Reschedule" bordered />
      <div className="page__body">
        {doctor.data ? <DoctorSummaryCard doctor={doctor.data} /> : <Skeleton height={104} />}
        {a && (
          <div className="reschedule-current">
            <span className="u-muted">Current</span>
            <strong>{`${formatDayDate(a.date)} · ${formatTime(a.time)}`}</strong>
          </div>
        )}
        {doctorId && <BookingScheduler picker={picker} />}
        {submitError && <div className="alert u-mt-4"><IconInfoCircle size={18} />{submitError}</div>}
      </div>

      <StickyFooter>
        <div className="sticky-footer__summary">
          <div>
            <p className="sticky-footer__label">New time</p>
            <p className="sticky-footer__value reschedule-summary">
              {a && <span className="u-subtle">{formatDayDate(a.date)}</span>}
              <IconArrowRight size={16} />
              <span>
                {selectedDate ? formatDayDate(selectedDate) : 'Pick a date'}
                {selectedTime ? `, ${formatTime(selectedTime)}` : ''}
              </span>
            </p>
          </div>
        </div>
        <Button block disabled={!selectedTime || unchanged} loading={submitting} onClick={save}>
          {selectedTime ? 'Confirm new time' : 'Select a time'}
        </Button>
      </StickyFooter>
    </div>
  );
}
